/**
 * GitTab - GitHub integration settings for a project
 *
 * A store-driven component that manages GitHub connection state and displays
 * appropriate UI based on the current state machine state.
 */

import { useEffect, useCallback, useState } from "react";
import { useGitStore, GitTabState, GitErrorCode, AuthType } from "@/stores/gitStore";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Github,
  RefreshCw,
  Unlink,
  Loader2,
  AlertTriangle,
  ExternalLink,
  Building2,
  User,
  Clock,
  Key,
  FolderGit2,
  LogOut,
} from "lucide-react";

import { InstallationPicker } from "./InstallationPicker";
import { RepoPickerModal } from "./RepoPickerModal";
import { PATRepoPickerModal } from "./PATRepoPickerModal";
import { BranchSelector } from "./BranchSelector";
import { CommitList } from "./CommitList";
import { PullRequestList } from "./PullRequestList";
import { CreateBranchModal } from "./CreateBranchModal";
import { CreatePRModal } from "./CreatePRModal";
import { PATSettings } from "./PATSettings";

interface GitTabProps {
  projectId: number;
  githubRepo: string | null;
  installationId: number | null;
  workspaceId?: number | null;
  onRepoChange?: (repo: string | null, installationId: number | null) => void;
}

// Error code to user-friendly message mapping
const ERROR_MESSAGES: Record<
  GitErrorCode,
  { title: string; description: string; action: "retry" | "reconnect" | "install" }
> = {
  RATE_LIMITED: {
    title: "Rate Limited",
    description: "GitHub API rate limit exceeded. Please wait before retrying.",
    action: "retry",
  },
  TOKEN_EXPIRED: {
    title: "Session Expired",
    description: "Your GitHub authentication has expired. Please reconnect.",
    action: "reconnect",
  },
  APP_NOT_INSTALLED: {
    title: "App Not Installed",
    description: "The Colony GitHub App is not installed on this repository.",
    action: "install",
  },
  INSTALLATION_REMOVED: {
    title: "Installation Removed",
    description: "The GitHub App installation has been removed. Please reinstall.",
    action: "install",
  },
  ACCESS_DENIED: {
    title: "Access Denied",
    description: "You don't have permission to access this repository.",
    action: "retry",
  },
  NOT_FOUND: {
    title: "Not Found",
    description: "The repository or resource could not be found.",
    action: "retry",
  },
  UNKNOWN: {
    title: "Error",
    description: "An unexpected error occurred.",
    action: "retry",
  },
};

export function GitTab({
  projectId,
  githubRepo,
  installationId,
  workspaceId,
  onRepoChange,
}: GitTabProps) {
  // Store state
  const {
    tabState,
    isLoading,
    isSaving,
    error,
    connectedInstallations,
    selectedInstallationId,
    selectedRepo,
    branches,
    selectedBranch,
    commits,
    pullRequests,
    hasMoreBranches,
    hasMoreCommits,
    hasMorePullRequests,
    authType,
    workspacePAT,
    currentWorkspaceId,
    // Actions
    initializeForProject,
    selectBranch,
    refreshAll,
    clearError,
    loadMoreBranches,
    loadMoreCommits,
    loadMorePullRequests,
    fetchCommits,
    setAuthType,
    disconnectInstallation,
    deletePAT,
    setTabState,
  } = useGitStore();

  // Local state for auth type toggle (before connection)
  const [localAuthType, setLocalAuthType] = useState<AuthType>(authType === "none" ? "installation" : authType);

  // Initialize on mount or when project changes
  useEffect(() => {
    if (workspaceId) {
      initializeForProject(workspaceId, githubRepo, installationId);
    }
  }, [workspaceId, githubRepo, installationId, initializeForProject]);

  // Sync local auth type with store (skip "none" — keep previous local type)
  useEffect(() => {
    if (authType !== "none") setLocalAuthType(authType);
  }, [authType]);

  // Parse owner/repo for API calls
  const [owner, repo] = (selectedRepo || githubRepo || "").split("/");
  const activeInstallationId = selectedInstallationId || installationId;

  // Get the connected installation info
  const connectedInstallation = connectedInstallations.find(
    (i) => i.id === activeInstallationId
  );

  // Check if we have valid auth for API calls
  const hasValidAuth = authType === "pat" ? !!workspacePAT : !!activeInstallationId;

  // Handlers
  const handleRepoSelect = useCallback(
    (repo: string, instId: number) => {
      onRepoChange?.(repo, instId);
    },
    [onRepoChange]
  );

  const handleDisconnect = useCallback(() => {
    onRepoChange?.(null, null);
  }, [onRepoChange]);

  const handleBranchSelect = useCallback(
    (branch: string | null) => {
      selectBranch(branch);
      if (branch && owner && repo) {
        fetchCommits(owner, repo, branch, true);
      }
    },
    [selectBranch, fetchCommits, owner, repo]
  );

  const handleRefresh = useCallback(() => {
    if (owner && repo) {
      refreshAll(owner, repo);
    }
  }, [refreshAll, owner, repo]);

  const handleRetry = useCallback(() => {
    clearError();
    if (workspaceId) {
      initializeForProject(workspaceId, githubRepo, installationId);
    }
  }, [clearError, initializeForProject, workspaceId, githubRepo, installationId]);

  const handleLoadMoreBranches = useCallback(() => {
    if (owner && repo) {
      loadMoreBranches(owner, repo);
    }
  }, [loadMoreBranches, owner, repo]);

  const handleLoadMoreCommits = useCallback(() => {
    if (owner && repo && selectedBranch) {
      loadMoreCommits(owner, repo, selectedBranch);
    }
  }, [loadMoreCommits, owner, repo, selectedBranch]);

  const handleLoadMorePullRequests = useCallback(() => {
    if (owner && repo) {
      loadMorePullRequests(owner, repo);
    }
  }, [loadMorePullRequests, owner, repo]);

  const handleAuthTypeChange = (value: string) => {
    const newType = value as AuthType;
    setLocalAuthType(newType);
    setAuthType(newType);
  };

  const handleDisconnectAccount = useCallback(async () => {
    // Disconnect the repo first
    onRepoChange?.(null, null);

    // Then remove the auth credentials
    if (authType === "pat" && workspaceId) {
      await deletePAT(workspaceId);
    } else if (authType === "installation" && workspaceId && activeInstallationId) {
      await disconnectInstallation(workspaceId, activeInstallationId);
    }

    // Force back to auth selection screen
    setTabState("NO_CONNECTION");
  }, [authType, workspaceId, activeInstallationId, onRepoChange, deletePAT, disconnectInstallation, setTabState]);

  // Colony card style applied to all Card components
  const colonyCardStyle = { background: "var(--gradient-card)", boxShadow: "var(--shadow-subtle)" } as const;
  const colonyCardClass = "rounded-[8px] border-[rgba(255,255,255,0.06)]";

  // Render error state
  if (tabState === "ERROR" && error) {
    const errorInfo = ERROR_MESSAGES[error.code] || ERROR_MESSAGES.UNKNOWN;

    return (
      <div className="space-y-6" style={{ color: "var(--colony-text-primary)" }}>
        <Card className={colonyCardClass} style={colonyCardStyle}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-colony-text-heading">
              <Github className="w-5 h-5" />
              GitHub Integration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>{errorInfo.title}</AlertTitle>
              <AlertDescription className="mt-2">
                <p className="mb-3">{error.message || errorInfo.description}</p>
                {error.retryAfter && (
                  <p className="text-sm mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Retry available in {error.retryAfter} seconds
                  </p>
                )}
                <div className="flex gap-2">
                  {errorInfo.action === "retry" && (
                    <Button variant="outline" size="sm" onClick={handleRetry}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Retry
                    </Button>
                  )}
                  {errorInfo.action === "reconnect" && (
                    <Button variant="outline" size="sm" onClick={handleDisconnect}>
                      <Unlink className="w-4 h-4 mr-2" />
                      Reconnect
                    </Button>
                  )}
                  {errorInfo.action === "install" && (
                    <Button variant="outline" size="sm" asChild>
                      <a
                        href="https://github.com/apps/factory-dev/installations/new"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Install App
                      </a>
                    </Button>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render loading state
  if (tabState === "LOADING") {
    return (
      <div className="space-y-6" style={{ color: "var(--colony-text-primary)" }}>
        <Card className={colonyCardClass} style={colonyCardStyle}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-colony-text-heading">
              <Github className="w-5 h-5" />
              GitHub Integration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-colony-text-tertiary" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render no connection state - show auth type picker
  if (tabState === "NO_CONNECTION") {
    return (
      <div className="space-y-6" style={{ color: "var(--colony-text-primary)" }}>
        <Card className={colonyCardClass} style={colonyCardStyle}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-colony-text-heading">
              <Github className="w-5 h-5" />
              GitHub Integration
            </CardTitle>
            <CardDescription className="text-colony-text-tertiary">
              Connect this project to a GitHub repository
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Auth Type Toggle */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Authentication Method</Label>
              <RadioGroup
                value={localAuthType}
                onValueChange={handleAuthTypeChange}
                className="grid grid-cols-2 gap-4"
              >
                <div
                  className={`relative flex cursor-pointer rounded-lg border p-4 ${
                    localAuthType === "installation"
                      ? "border-primary bg-primary/5"
                      : "border-muted hover:border-muted-foreground/25"
                  }`}
                  onClick={() => handleAuthTypeChange("installation")}
                >
                  <RadioGroupItem
                    value="installation"
                    id="auth-installation"
                    className="sr-only"
                  />
                  <div className="flex flex-col gap-1">
                    <Label
                      htmlFor="auth-installation"
                      className="flex items-center gap-2 cursor-pointer font-medium"
                    >
                      <Github className="w-4 h-4" />
                      GitHub App
                      <Badge variant="secondary" className="text-xs">
                        Recommended
                      </Badge>
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Secure app-based authentication with fine-grained permissions
                    </p>
                  </div>
                </div>

                <div
                  className={`relative flex cursor-pointer rounded-lg border p-4 ${
                    localAuthType === "pat"
                      ? "border-primary bg-primary/5"
                      : "border-muted hover:border-muted-foreground/25"
                  }`}
                  onClick={() => handleAuthTypeChange("pat")}
                >
                  <RadioGroupItem value="pat" id="auth-pat" className="sr-only" />
                  <div className="flex flex-col gap-1">
                    <Label
                      htmlFor="auth-pat"
                      className="flex items-center gap-2 cursor-pointer font-medium"
                    >
                      <Key className="w-4 h-4" />
                      Personal Access Token
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Use a GitHub PAT for authentication
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </div>

            {/* Auth Type Specific UI */}
            {workspaceId && (
              <>
                {localAuthType === "installation" ? (
                  <InstallationPicker workspaceId={workspaceId} />
                ) : (
                  <PATSettings workspaceId={workspaceId} onConnect={handleRetry} />
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render no repo state - auth connected, need to select repo
  if (tabState === "NO_REPO") {
    return (
      <div className="space-y-6" style={{ color: "var(--colony-text-primary)" }}>
        <Card className={colonyCardClass} style={colonyCardStyle}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-colony-text-heading">
              <Github className="w-5 h-5" />
              GitHub Integration
            </CardTitle>
            <CardDescription className="text-colony-text-tertiary">Select a repository to link to this project</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Connected Installation Info */}
            {authType === "installation" && connectedInstallation && (
              <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/50">
                {connectedInstallation.accountAvatarUrl ? (
                  <img
                    src={connectedInstallation.accountAvatarUrl}
                    alt={connectedInstallation.accountLogin}
                    className="w-8 h-8 rounded-full"
                  />
                ) : connectedInstallation.accountType === "Organization" ? (
                  <Building2 className="w-8 h-8 text-muted-foreground" />
                ) : (
                  <User className="w-8 h-8 text-muted-foreground" />
                )}
                <div className="flex-1">
                  <p className="font-medium">{connectedInstallation.accountLogin}</p>
                  <p className="text-sm text-muted-foreground">
                    {connectedInstallation.accountType}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDisconnectAccount}
                >
                  <LogOut className="w-3.5 h-3.5 mr-1.5" />
                  Disconnect
                </Button>
              </div>
            )}

            {/* PAT Info */}
            {authType === "pat" && workspacePAT && (
              <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/50">
                <Key className="w-8 h-8 text-muted-foreground" />
                <div className="flex-1">
                  <p className="font-medium">Personal Access Token</p>
                  <p className="text-sm text-muted-foreground">
                    {workspacePAT.githubLogin ? `@${workspacePAT.githubLogin}` : "Connected"}
                    {workspacePAT.tokenHint && ` - ${workspacePAT.tokenHint}`}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDisconnectAccount}
                >
                  <LogOut className="w-3.5 h-3.5 mr-1.5" />
                  Disconnect
                </Button>
              </div>
            )}

            {/* Repo Selection */}
            {authType === "installation" && workspaceId && activeInstallationId && (
              <RepoPickerModal
                workspaceId={workspaceId}
                installationId={activeInstallationId}
                onSelect={handleRepoSelect}
                trigger={
                  <Button className="w-full">
                    <FolderGit2 className="w-4 h-4 mr-2" />
                    Select Repository
                  </Button>
                }
              />
            )}

            {/* Repo selection for PAT mode */}
            {authType === "pat" && workspaceId && (
              <PATRepoPickerModal
                workspaceId={workspaceId}
                onSelect={(repo) => {
                  onRepoChange?.(repo, null);
                }}
              />
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render ready state - full UI
  const branchData = branches?.data || [];
  const commitData = commits?.data || [];
  const prData = pullRequests?.data || [];

  return (
    <div className="space-y-6" style={{ color: "var(--colony-text-primary)" }}>
      {/* Repository Header */}
      <Card className={colonyCardClass} style={colonyCardStyle}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-colony-text-heading">
              <Github className="w-5 h-5" />
              {selectedRepo || githubRepo}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRefresh}
                disabled={isLoading}
                aria-label="Refresh repository"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
              </Button>
              <Button variant="ghost" size="icon" onClick={handleDisconnect} aria-label="Unlink repository">
                <Unlink className="w-4 h-4" />
              </Button>
            </div>
          </div>
          {/* Connected account info */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
            {authType === "installation" && connectedInstallation && (
              <>
                {connectedInstallation.accountAvatarUrl ? (
                  <img
                    src={connectedInstallation.accountAvatarUrl}
                    alt={connectedInstallation.accountLogin}
                    className="w-4 h-4 rounded-full"
                  />
                ) : (
                  <Building2 className="w-3.5 h-3.5" />
                )}
                <span>{connectedInstallation.accountLogin}</span>
              </>
            )}
            {authType === "pat" && workspacePAT && (
              <>
                <Key className="w-3.5 h-3.5" />
                <span>{workspacePAT.githubLogin ? `@${workspacePAT.githubLogin}` : "PAT"}</span>
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={handleDisconnectAccount}
            >
              <LogOut className="w-3.5 h-3.5 mr-1.5" />
              Disconnect
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Branch Selector Row */}
          <div className="flex items-center justify-between gap-4">
            <BranchSelector
              branches={branchData}
              selectedBranch={selectedBranch}
              onSelect={handleBranchSelect}
              hasMore={hasMoreBranches}
              isLoading={isLoading}
              onLoadMore={handleLoadMoreBranches}
            />
            <div className="flex items-center gap-2">
              {owner && repo && workspaceId && hasValidAuth && branchData.length > 0 && (
                <>
                  <CreateBranchModal
                    owner={owner}
                    repo={repo}
                    branches={branchData}
                  />
                  <CreatePRModal
                    owner={owner}
                    repo={repo}
                    branches={branchData}
                    currentBranch={selectedBranch}
                  />
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Commits and PRs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CommitList
          commits={commitData}
          isLoading={isLoading}
          hasMore={hasMoreCommits}
          onLoadMore={handleLoadMoreCommits}
        />
        <PullRequestList
          pullRequests={prData}
          isLoading={isLoading}
          hasMore={hasMorePullRequests}
          onLoadMore={handleLoadMorePullRequests}
        />
      </div>
    </div>
  );
}
